/**
 * Grouping for the Growing layer.
 *
 * Regions are grouped by the administrative region they belong to, as the
 * warehouse records it: the rows carry country, region and city, and a pin
 * stands for one region's worth of cities. So a group is a real place with a
 * name — "British Columbia" — rather than a screen-space accident.
 *
 * This replaced a pixel-proximity clustering that merged whatever happened to
 * overlap at the current zoom. Two consequences worth knowing:
 *
 *  - Grouping no longer depends on the view, so it survives panning and
 *    zooming and need not be recomputed when the map moves.
 *  - Zooming in therefore never splits a group. Separating a group's pins is
 *    now an explicit action — the overview's "Expand" — rather than something
 *    the user discovers by scrolling the wheel.
 */

/** Status precedence when a cluster has no single majority. */
const STATUS_RANK = ['producing', 'ending_soon', 'starting_soon', 'not_producing'];

/**
 * The status a mixed cluster paints itself with: the most common one, ties
 * broken by `STATUS_RANK` so the same cluster never flickers between colours
 * across renders.
 */
export function dominantStatus(counts) {
  let best = null;
  let bestN = -1;
  for (const status of STATUS_RANK) {
    const n = counts[status] || 0;
    if (n > bestN) { best = status; bestN = n; }
  }
  return bestN > 0 ? best : null;
}

// `Number(null)` is 0 and `Number('')` is 0, both finite — so a null-island
// check has to reject the empty values before it converts, or a row with no
// coordinates silently becomes a pin at 0,0 in the Gulf of Guinea.
const isCoord = (v) => v != null && v !== '' && Number.isFinite(Number(v));
const hasPosition = (r) => isCoord(r?.latitude) && isCoord(r?.longitude);

const norm = (v) => String(v ?? '').trim().toLowerCase();

/**
 * A group's map position: the mean of its members' coordinates.
 *
 * Longitude is averaged as a direction rather than as a number. A region
 * straddling the antimeridian has members at +179 and -179, whose arithmetic
 * mean is 0 — the middle of the map, half a world from either of them.
 */
function centroid(rows) {
  let lat = 0;
  let x = 0;
  let y = 0;
  for (const r of rows) {
    lat += Number(r.latitude);
    const rad = (Number(r.longitude) * Math.PI) / 180;
    x += Math.cos(rad);
    y += Math.sin(rad);
  }
  const n = rows.length;
  return [lat / n, (Math.atan2(y / n, x / n) * 180) / Math.PI];
}

/**
 * @param {object[]} regions  Rows carrying `country`, `region`, `specific_location`
 *   and coordinates.
 * @returns {Array<{key: string, regions: object[], position: [number, number],
 *   counts: Record<string, number>, total: number, status: string|null}>}
 *   Single-location groups are returned too, as groups of one — the caller
 *   decides whether a lone location draws as a plain pin.
 */
export function groupRegionsByArea(regions) {
  if (!Array.isArray(regions) || regions.length === 0) return [];

  const groups = new Map();
  for (const r of regions) {
    if (!hasPosition(r)) continue;
    const region = norm(r.region);
    // Region within country: a bare region name is not unique, and plenty of
    // countries have a "Central" or a "Northern" province.
    // A row with no region of its own cannot honestly be pooled with
    // anything, so it is keyed to its own location and draws as a lone pin.
    const key = region
      ? `region|${norm(r.country)}|${region}`
      : `location|${norm(r.country)}|${norm(r.specific_location)}|${r.latitude}|${r.longitude}`;
    let g = groups.get(key);
    if (!g) {
      g = { key, regions: [], counts: {} };
      groups.set(key, g);
    }
    g.regions.push(r);
    const status = r.harvest_status;
    if (status) g.counts[status] = (g.counts[status] || 0) + 1;
  }

  return [...groups.values()].map((g) => ({
    key: g.key,
    regions: g.regions,
    position: centroid(g.regions),
    counts: g.counts,
    total: g.regions.length,
    status: dominantStatus(g.counts),
  }));
}

export default groupRegionsByArea;
